import React from "react";
import { Star, MapPin, Users, BedDouble } from "lucide-react";
import { ROOMS } from "../../data/site";
import { appendUtms, trackAirbnbClick } from "../../lib/analytics";

const AIRBNB_URL = "https://www.airbnb.com/rooms/1714671107106561677?guests=1&adults=1&s=67&unique_share_id=b6435ff4-4e39-4f5f-9b7c-5a533dfe7172";

const SHREK = ROOMS.find((r) => r.key === "shrek");
const MINIONS = ROOMS.find((r) => r.key === "minions");

const Star4 = ({ className, color = "#FFD166" }) => (
  <svg viewBox="0 0 24 24" className={className} fill={color} xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2c1.4 5 4.6 8.2 9.6 9.6C16.6 13 13.4 16.2 12 21.2 10.6 16.2 7.4 13 2.4 11.6 7.4 10.2 10.6 7 12 2z"/>
  </svg>
);

const Squiggle = ({ className, color = "#0f172a" }) => (
  <svg viewBox="0 0 120 20" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 10 Q 17 -2 32 10 T 62 10 T 92 10 T 118 10" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none"/>
  </svg>
);

const RoomPanel = ({ room, rotate, color, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border-4 border-slate-900 rounded-3xl p-2 sm:p-2.5 neo-shadow-lg relative animate-pop-in"
    style={{ transform: `rotate(${rotate}deg)` }}
  >
    <div className="relative overflow-hidden rounded-2xl">
      <img
        src={room.image}
        alt={`${room.name} — themed bedroom`}
        className="w-full aspect-[3/4] object-cover"
        loading="eager"
      />
    </div>
    <div className="flex items-center justify-between mt-2 px-1">
      <p className="font-fredoka font-semibold text-sm sm:text-base">{room.name.replace(" Room", "")}</p>
      <span className="text-xl">{room.emoji}</span>
    </div>
    <span
      className="absolute -top-3 -right-3 w-9 h-9 sm:w-10 sm:h-10 rounded-full border-4 border-slate-900 grid place-items-center text-white font-fredoka font-bold text-sm neo-shadow-sm"
      style={{ background: color }}
    >
      ★
    </span>
  </div>
);

export default function Hero({ onBook }) {
  return (
    <section id="top" data-testid="hero-section" className="relative pt-28 lg:pt-32 pb-12 overflow-hidden">
      {/* decorative background */}
      <div className="absolute inset-0 dotted-bg opacity-30 pointer-events-none" />
      <Star4 className="absolute top-32 left-8 w-8 h-8 animate-float-y" color="#FFD166" />
      <Star4 className="absolute top-40 right-10 w-6 h-6 animate-wiggle" color="#EF476F" />
      <Star4 className="absolute bottom-10 left-1/4 w-7 h-7 animate-float-y" color="#06D6A0" />
      <Squiggle className="absolute top-28 left-1/2 w-32 opacity-50" color="#3B82F6" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center relative">
        {/* LEFT — copy + CTAs */}
        <div>
          <div data-testid="hero-badge" className="inline-flex items-center gap-2 bg-white border-4 border-slate-900 rounded-full px-4 py-1.5 neo-shadow-sm rotate-[-2deg] mb-6">
            <MapPin size={16} className="text-brand-red" />
            <span className="font-fredoka font-semibold text-sm">A Universal-Inspired Themed Home</span>
          </div>

          <h1 data-testid="hero-headline" className="font-fredoka font-bold text-slate-900 text-[2.5rem] leading-[1.02] sm:text-6xl lg:text-[4.25rem] sm:leading-[0.98] tracking-tight">
            Your family&rsquo;s next{" "}
            <span className="inline-block relative">
              <span className="relative z-10 px-2 bg-brand-yellow border-4 border-slate-900 rounded-2xl">favorite</span>
            </span>{" "}
            getaway.
          </h1>

          <p data-testid="hero-subheadline" className="mt-6 max-w-xl text-lg sm:text-xl text-slate-700 leading-relaxed">
            An entire themed home in Frisco, Texas, with{" "}
            <span className="font-bold text-slate-900">four unforgettable bedrooms</span> and room for up to 12 guests.
          </p>

          {/* Whole-home clarity line */}
          <div
            data-testid="hero-wholehome-line"
            className="mt-6 inline-flex items-center gap-2 bg-brand-green text-slate-900 border-4 border-slate-900 rounded-full px-4 py-2 neo-shadow-sm font-fredoka font-bold text-sm sm:text-base"
          >
            Entire home · 4 themed bedrooms · Sleeps up to 12
          </div>

          <div className="mt-8 flex flex-col items-start gap-3">
            <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
              <button
                data-testid="hero-book-btn"
                onClick={onBook}
                className="font-fredoka font-bold text-xl bg-brand-red text-white border-4 border-slate-900 rounded-full px-8 py-4 neo-shadow neo-press"
              >
                Check Availability →
              </button>
              <a
                data-testid="hero-rooms-btn"
                href="#rooms"
                className="font-fredoka font-bold text-xl bg-brand-yellow text-slate-900 border-4 border-slate-900 rounded-full px-8 py-4 neo-shadow neo-press"
              >
                Peek the Rooms
              </a>
            </div>

            {/* Booking handoff note */}
            <p data-testid="hero-handoff-note" className="max-w-md font-quicksand text-sm text-slate-600">
              Reservations are completed securely on our property manager{" "}
              <span className="font-semibold text-slate-800">Home Team VR</span>&rsquo;s booking page. Prefer Airbnb?{" "}
              <a
                data-testid="hero-airbnb-link"
                href={AIRBNB_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  const finalUrl = appendUtms(AIRBNB_URL);
                  trackAirbnbClick({ destinationUrl: finalUrl, source: "hero_subtext" });
                  window.open(finalUrl, "_blank", "noopener,noreferrer");
                }}
                className="font-semibold text-brand-red hover:text-slate-900 underline underline-offset-4 transition-colors"
              >
                View on Airbnb
              </a>
            </p>
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} className="fill-brand-yellow text-brand-yellow" />
                ))}
              </div>
              <span className="font-quicksand font-semibold text-slate-700">4.8-star superhost with 65+ listings</span>
            </div>
            <div className="flex items-center gap-2 font-quicksand font-semibold text-slate-700">
              <Users size={18} /> Sleeps 12
            </div>
            <div className="flex items-center gap-2 font-quicksand font-semibold text-slate-700">
              <BedDouble size={18} /> 4 themed bedrooms
            </div>
          </div>
        </div>

        {/* RIGHT — Shrek (left) + Minions (right) side by side */}
        <div data-testid="hero-room-panels" className="grid grid-cols-2 gap-4 sm:gap-5 lg:gap-6">
          <RoomPanel room={SHREK} rotate={-2} color="#06D6A0" testid="hero-panel-shrek" />
          <RoomPanel room={MINIONS} rotate={2} color="#FFD166" testid="hero-panel-minions" />
        </div>
      </div>
    </section>
  );
}
